import {
  shopifyProductQueryKey,
  shopifyProductQueryOptions,
  type ShopifyProduct,
} from '@/hooks/queries/use-shopify-products-query';
import {
  ErrorCode,
  GiftReofferCatalogStatus,
  type GiftReofferReviewProduct,
} from '@fluenceur/types';
import type { QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

type LiveProductVariant = {
  available: boolean;
  inventoryQuantity: number | null;
};

/**
 * Shopify's `available` is not sufficient for a product just added to a
 * re-offer: tracked inventory at zero still needs the explicit override.
 * A null quantity means inventory is untracked, so Shopify availability wins.
 */
export const liveProductVariantReofferStatus = ({
  available,
  inventoryQuantity,
}: LiveProductVariant): GiftReofferCatalogStatus =>
  !available || (inventoryQuantity !== null && inventoryQuantity <= 0)
    ? GiftReofferCatalogStatus.OUT_OF_STOCK
    : GiftReofferCatalogStatus.AVAILABLE;

const statusForSelectedVariants = (
  variants: GiftReofferReviewProduct['variants'],
  selectedVariantIds: string[]
): GiftReofferCatalogStatus => {
  const selected = variants.filter((variant) =>
    selectedVariantIds.includes(variant.shopifyVariantId)
  );
  if (selected.some((variant) => variant.status === GiftReofferCatalogStatus.MISSING))
    return GiftReofferCatalogStatus.MISSING;
  if (selected.some((variant) => variant.status === GiftReofferCatalogStatus.NOT_ORDERABLE))
    return GiftReofferCatalogStatus.NOT_ORDERABLE;
  if (selected.some((variant) => variant.status === GiftReofferCatalogStatus.OUT_OF_STOCK))
    return GiftReofferCatalogStatus.OUT_OF_STOCK;
  return GiftReofferCatalogStatus.AVAILABLE;
};

const isOrderableShopifyProduct = (product: ShopifyProduct) =>
  product.status === 'ACTIVE' || product.status === 'UNLISTED';

/** Matches backend aggregation: any selected OOS variant makes its product OOS. */
export const withGiftReofferProductSelectionStatus = (
  product: GiftReofferReviewProduct,
  selectedVariantIds: string[]
): GiftReofferReviewProduct => ({
  ...product,
  status:
    product.status === GiftReofferCatalogStatus.MISSING ||
    product.status === GiftReofferCatalogStatus.NOT_ORDERABLE
      ? product.status
      : statusForSelectedVariants(product.variants, selectedVariantIds),
});

export const giftReofferProductFromShopifyProduct = (
  product: ShopifyProduct
): GiftReofferReviewProduct => {
  const variants = product.variants.map((variant) => ({
    shopifyVariantId: variant.id,
    title: variant.title,
    priceInCents: variant.priceInCents,
    available: variant.available,
    inventoryQuantity: variant.inventoryQuantity,
    status: liveProductVariantReofferStatus(variant),
  }));
  return withGiftReofferProductSelectionStatus(
    {
      shopifyProductId: product.id,
      title: product.title,
      imageUrl: product.featuredImageUrl ?? undefined,
      status: isOrderableShopifyProduct(product)
        ? GiftReofferCatalogStatus.AVAILABLE
        : GiftReofferCatalogStatus.NOT_ORDERABLE,
      variants,
    },
    variants.map((variant) => variant.shopifyVariantId)
  );
};

/**
 * Preserve selections while replacing live attributes after a rejected
 * submission. Missing Shopify products and variants stay visible as MISSING.
 */
export const mergeGiftReofferProductWithLiveProduct = (
  current: GiftReofferReviewProduct,
  live: ShopifyProduct | null
): GiftReofferReviewProduct => {
  if (!live) {
    return {
      ...current,
      status: GiftReofferCatalogStatus.MISSING,
      variants: current.variants.map((variant) => ({
        ...variant,
        status: GiftReofferCatalogStatus.MISSING,
      })),
    };
  }
  const liveVariants = new Map(live.variants.map((variant) => [variant.id, variant]));
  const variants = current.variants.map((variant) => {
    const latest = liveVariants.get(variant.shopifyVariantId);
    if (!latest) return { ...variant, status: GiftReofferCatalogStatus.MISSING };
    return {
      ...variant,
      title: latest.title,
      priceInCents: latest.priceInCents,
      available: latest.available,
      inventoryQuantity: latest.inventoryQuantity,
      status: liveProductVariantReofferStatus(latest),
    };
  });
  return withGiftReofferProductSelectionStatus(
    {
      ...current,
      title: live.title,
      imageUrl: live.featuredImageUrl ?? undefined,
      status: isOrderableShopifyProduct(live)
        ? GiftReofferCatalogStatus.AVAILABLE
        : GiftReofferCatalogStatus.NOT_ORDERABLE,
      variants,
    },
    current.variants.map((variant) => variant.shopifyVariantId)
  );
};

export const refreshGiftReofferAddedProducts = async (
  queryClient: QueryClient,
  products: GiftReofferReviewProduct[]
): Promise<GiftReofferReviewProduct[]> =>
  Promise.all(
    products.map(async (product) => {
      try {
        await queryClient.invalidateQueries({
          queryKey: shopifyProductQueryKey(product.shopifyProductId),
        });
        const live = await queryClient.fetchQuery({
          ...shopifyProductQueryOptions(product.shopifyProductId),
          retry: false,
        });
        return mergeGiftReofferProductWithLiveProduct(product, live);
      } catch (error) {
        if (
          isAxiosError(error) &&
          (error.response?.status === 404 || error.response?.data?.code === ErrorCode.E_NOT_FOUND)
        ) {
          return mergeGiftReofferProductWithLiveProduct(product, null);
        }
        throw error;
      }
    })
  );
